/**
 * Full-page Google sign-in screen — visual style matches the DECE Snapshot
 * Tool's LoginPage (cream gradient background + glass card with gold accent
 * and navy heading).
 */

import { useEffect, useState } from 'react';
import { initGoogleSignIn } from '@/lib/auth/google';
import { useAuth } from '@/lib/auth/AuthContext';

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const DECE_LOGO =
  'https://solved-forms.s3.us-east-2.amazonaws.com/nyc_dece_logo.png';

export function SignInScreen() {
  const { setSession } = useAuth();
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!CLIENT_ID) {
      setErr('Google sign-in is not configured (missing VITE_GOOGLE_CLIENT_ID).');
      return;
    }
    initGoogleSignIn(CLIENT_ID, 'google-signin-btn', (session) => {
      setSession(session);
    }).catch((e) => {
      setErr(e instanceof Error ? e.message : 'Failed to load Google sign-in');
    });
  }, [setSession]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'linear-gradient(145deg, #f7f4ec 0%, #eae5d6 50%, #f0ede4 100%)',
        padding: 20,
      }}
    >
      <div
        style={{
          background: 'rgba(255,255,255,0.88)',
          border: '1px solid rgba(255,255,255,0.70)',
          borderRadius: 28,
          boxShadow: '0 12px 48px -12px rgba(24,32,51,0.18)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          padding: '48px 40px',
          maxWidth: 420,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <img
          src={DECE_LOGO}
          alt="NYC DECE"
          style={{ height: 56, margin: '0 auto 16px' }}
        />

        <p
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: '#9a8044',
            marginBottom: 8,
          }}
        >
          FCCERS-3 Observation Tool
        </p>

        <h1
          style={{
            fontSize: 24,
            fontWeight: 700,
            color: '#1a2744',
            letterSpacing: '-0.03em',
            marginBottom: 8,
          }}
        >
          Sign in to continue
        </h1>

        <p
          style={{
            fontSize: 14,
            color: '#6b7280',
            lineHeight: 1.5,
            marginBottom: 32,
          }}
        >
          Use your DECE or organization email to submit observations and
          access your dashboard.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div id="google-signin-btn" />
        </div>

        {err && (
          <div
            style={{
              marginTop: 20,
              padding: '12px 16px',
              borderRadius: 12,
              background: '#fef2f2',
              border: '1px solid #fecaca',
              fontSize: 13,
              color: '#b91c1c',
              textAlign: 'left',
            }}
          >
            {err}
          </div>
        )}

        <div
          style={{
            marginTop: 32,
            paddingTop: 20,
            borderTop: '1px solid rgba(26,39,68,0.08)',
          }}
        >
          <p
            style={{
              fontSize: 11,
              color: '#6b7280',
              lineHeight: 1.6,
              margin: 0,
            }}
          >
            Your access level is determined automatically based on your role.
            <br />
            <strong>Admins</strong> see all observations.{' '}
            <strong>Observers</strong> see their own.
          </p>
        </div>
      </div>
    </div>
  );
}
